import React from 'react';
import { Box } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import ShowChartRoundedIcon from '@mui/icons-material/ShowChartRounded';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import GroupsIcon from '@mui/icons-material/Groups';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import StoreIcon from '@mui/icons-material/Store';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import AddIcon from '@mui/icons-material/Add';
import {
  DashboardScroller,
  DashboardStack,
  DashboardRow,
  DashboardCardGroup,
  DashboardCard,
  DashboardIconBox,
  DashboardLabel,
  DashboardValue,
  DashboardButtonArea,
  DashboardButtonGroup,
  PositionManageButton,
  CreateButton,
} from './EmployeeSettingStyled';

const SUMMARY_CARDS = [
  { key: 'total', label: '전체 임직원', icon: PersonIcon, iconBg: 'rgba(74,179,188,0.16)', iconColor: '#4ab3bc' },
  { key: 'counting', label: '횟수제 강사', icon: ShowChartRoundedIcon, iconBg: 'rgba(85,201,130,0.16)', iconColor: '#55c982' },
  { key: 'monthly', label: '월급제 직원', icon: CalendarTodayOutlinedIcon, iconBg: 'rgba(53,130,192,0.16)', iconColor: '#3582c0', disabled: true },
  { key: 'hourly', label: '시간제 직원', icon: AccessTimeIcon, iconBg: 'rgba(255,182,89,0.16)', iconColor: '#ffb659', disabled: true },
];

const DETAIL_CARDS_LEFT = [
  { key: 'working', label: '재직중', icon: BadgeOutlinedIcon, iconBg: 'rgba(74,179,188,0.16)', iconColor: '#4ab3bc' },
  { key: 'left', label: '퇴사', icon: LogoutIcon, iconBg: 'rgba(153,153,153,0.16)', iconColor: '#999' },
];

const DETAIL_CARDS_CENTER = [
  { key: 'privateLessonCount', label: '개인레슨', icon: DirectionsRunIcon, iconBg: 'rgba(88,86,214,0.16)', iconColor: '#5856d6' },
  { key: 'groupLessonCount', label: '그룹레슨', icon: GroupsIcon, iconBg: 'rgba(50,173,230,0.16)', iconColor: '#32ade6' },
];

const DETAIL_CARDS_RIGHT = [
  { key: 'customerManagementCount', label: '고객관리', icon: SupportAgentIcon, iconBg: 'rgba(255,149,0,0.16)', iconColor: '#ff9500' },
  { key: 'storeManagementCount', label: '매장관리', icon: StoreIcon, iconBg: 'rgba(255,59,48,0.16)', iconColor: '#ff3b30' },
];

const DashboardCardItem = ({ label, value, icon: Icon, iconBg, iconColor, disabled }) => (
  <DashboardCard sx={disabled ? { opacity: 0.5, position: 'relative' } : {}}>
    <DashboardIconBox bgColor={iconBg}>
      <Icon sx={{ color: iconColor }} />
    </DashboardIconBox>
    <Box>
      <DashboardLabel>{label}</DashboardLabel>
      {disabled
        ? <DashboardValue sx={{ fontSize: '12px', color: '#999', fontWeight: 400 }}>준비중입니다</DashboardValue>
        : <DashboardValue>{value}명</DashboardValue>
      }
    </Box>
  </DashboardCard>
);

const EmployeeSettingDashboard = ({ stats, canCreate, onPositionManage, onAddExistingEmployee }) => {
  return (
    <DashboardScroller>
      <DashboardStack>
        {/* Row 1: 전체 임직원 / 횟수제 / 월급제 / 시간제 */}
        <DashboardRow>
          {SUMMARY_CARDS.map(card => (
            <DashboardCardItem
              key={card.key}
              label={card.label}
              value={stats[card.key] ?? 0}
              icon={card.icon}
              iconBg={card.iconBg}
              iconColor={card.iconColor}
              disabled={card.disabled}
            />
          ))}
        </DashboardRow>

        {/* Row 2: 상세 통계 + 버튼 */}
        <DashboardRow>
          {/* 재직중 / 퇴사 */}
          <DashboardCardGroup>
            {DETAIL_CARDS_LEFT.map(card => (
              <DashboardCardItem
                key={card.key}
                label={card.label}
                value={stats[card.key] ?? 0}
                icon={card.icon}
                iconBg={card.iconBg}
                iconColor={card.iconColor}
              />
            ))}
          </DashboardCardGroup>

          {/* 개인레슨 / 그룹레슨 */}
          <DashboardCardGroup>
            {DETAIL_CARDS_CENTER.map(card => (
              <DashboardCardItem
                key={card.key}
                label={card.label}
                value={stats[card.key] ?? 0}
                icon={card.icon}
                iconBg={card.iconBg}
                iconColor={card.iconColor}
              />
            ))}
          </DashboardCardGroup>

          {/* 고객관리 / 매장관리 */}
          <DashboardCardGroup>
            {DETAIL_CARDS_RIGHT.map(card => (
              <DashboardCardItem
                key={card.key}
                label={card.label}
                value={stats[card.key] ?? 0}
                icon={card.icon}
                iconBg={card.iconBg}
                iconColor={card.iconColor}
              />
            ))}
          </DashboardCardGroup>

          {/* 직급관리 / 임직원 등록 버튼 */}
          <DashboardButtonArea>
            <DashboardButtonGroup>
              <PositionManageButton variant="contained" onClick={onPositionManage}>
                직급관리
              </PositionManageButton>
              {/* 신규 등록 아이디 중복확인 미작동 버그로 임시 비활성화 (전화번호 중복 가입 사고, 2026-10-01).
                  "강사 검색으로 추가"만 남아 선택지가 하나뿐이라 드롭다운 없이 바로 그 모달을 연다. */}
              {canCreate && (
                <CreateButton variant="contained" onClick={onAddExistingEmployee}>
                  <AddIcon sx={{ fontSize: 20 }} />
                  임직원 등록
                </CreateButton>
              )}
            </DashboardButtonGroup>
          </DashboardButtonArea>
        </DashboardRow>
      </DashboardStack>
    </DashboardScroller>
  );
};

export default EmployeeSettingDashboard;
